import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from "../../components/ui/card";
import { useInitialVisitStore } from "../../stores/initialVisitStore";
import { Button } from "../../components/ui/button";
import { DateTime } from "luxon";
import { InitialVisitBooking, useInitialVisitDetails } from "../../hooks/useInitialVisitDetails";
import { useState } from "react";
import { CircleAlertIcon, Loader2 } from "lucide-react";

export function BookingConfirmation() {
  const [bookingError, setBookingError] = useState<string | null>(null);

  const {
    selectedSlot,
    setSelectedSlot,
    setDate,
    setVisibleMonth,
    bookingStatus,
    setBookingStatus,
    providerOptions
  } = useInitialVisitStore();

  if (!selectedSlot) {
    return null;
  }

  const { data: initialVisitDetails } = useInitialVisitDetails({
    uniqueId: selectedSlot.uniqueId,
    enabled: Boolean(selectedSlot.uniqueId)
  });

  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = () => {
    setIsLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/schedule`, {
      method: "POST",
      body: JSON.stringify({
        uniqueId: selectedSlot.uniqueId,
        startDate: selectedSlot.start,
        endDate: selectedSlot.end,
        providerId: selectedSlot.providerId,
        appointmentTypeId: (initialVisitDetails as InitialVisitBooking)?.appointment.appointmentTypeId,
        caseStudyId: (initialVisitDetails as InitialVisitBooking)?.appointment.caseStudyId
      }),
      headers: {
        "Content-Type": "application/json"
      }
    })
      .then((res) => {
        if (res.status !== 200) {
          throw new Error("Failed to schedule appointment");
        }
        setBookingStatus("confirmed");
      })
      .catch((err) => {
        console.log("Error scheduling appointment", err);
        setBookingError(err.message);
        setBookingStatus("error");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <main className="p-8 min-h-svh bg-accent dark:bg-background flex flex-col items-center justify-center">
      <Card className="w-full max-w-xl relative">
        <CardHeader className="gap-1">
          <CardTitle className="text-muted-foreground font-medium text-sm">
            {initialVisitDetails?.organization.name}
          </CardTitle>
          <CardDescription className="text-foreground header-3">
            Initial Examination
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border-t border-b">
            <dl className="divide-y divide-border">
              <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                <dt className="text-sm/6 font-medium">Provider</dt>
                <dd className="mt-1 text-sm/6 text-muted-foreground sm:col-span-2 sm:mt-0 font-medium">
                  {
                    providerOptions.find(
                      (option) => option.value === selectedSlot.providerId
                    )?.label
                  }
                </dd>
              </div>
              <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                <dt className="text-sm/6 font-medium">Date</dt>
                <dd className="mt-1 text-sm/6 text-muted-foreground sm:col-span-2 sm:mt-0 font-medium">
                  {selectedSlot.start.toFormat("h:mm a - MMM d, yyyy")}
                </dd>
              </div>
              <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                <dt className="text-sm/6 font-medium">Duration</dt>
                <dd className="mt-1 text-sm/6 text-muted-foreground sm:col-span-2 sm:mt-0 font-medium">
                  {selectedSlot.end.diff(selectedSlot.start).as("minutes")} min
                </dd>
              </div>
            </dl>
          </div>
          {bookingStatus === "error" && (
            <div className="py-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="flex items-center justify-center size-8 rounded-full bg-destructive/10">
                  <CircleAlertIcon className="size-5 text-destructive stroke-[1.75]" />
                </div>
                <p className="font-medium">Failed to schedule appointment</p>
              </div>
              <p className="text-sm/6 text-muted-foreground">
                {bookingError === "Slot is no longer available"
                  ? "This slot is no longer available. Please select a different slot."
                  : "There was an error scheduling your appointment. Please try again or contact the clinic for help."}
              </p>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => {
              setSelectedSlot(null);
              setDate(DateTime.now().startOf("day"));
              setVisibleMonth(DateTime.now().startOf("month"));
              setBookingStatus("initial");
              setBookingError(null);
            }}
          >
            Back
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading && <Loader2 className="size-4 animate-spin" />}
            Confirm
          </Button>
        </CardFooter>
      </Card>
    </main>
  );
}
